import type { RefObject } from 'react';

import { IdsIcon } from '../../components/icon/Icon';
import Paragraph from '../../components/paragraph/Paragraph';
import type { Action, Content, ContentText, ContentView, Icon, PriceDetails } from '../../global-types/Content';
import { renderContent } from '../../utils/renderContent';

type DrawerContentProps = {
  view: ContentView;
  texts?: ContentText[];
  trackVisibilityElement: RefObject<null>;
  disableButton: boolean;
  setDisableButton: (value: boolean) => void;
  dispatchActionEvent: (action: Action, nextContent?: Content | null) => void;
};

type PriceDetailsProps = {
  icons: Icon[] | null;
  priceDetails: PriceDetails;
};

function PriceDetails({ icons, priceDetails }: PriceDetailsProps) {
  return (
    <div className="if card filled price-summary">
      <div className="if card-content">
        <div className="if flex drawer-gap-40 drawer-flex-row flex-column gap-16">
          <div className="if flex gap-8 align-items-center">
            {icons?.[0] && <IdsIcon name={icons[0]} size={24} />}
            <Paragraph text={priceDetails.productName ?? ''} />
          </div>
          <div className="if flex gap-8 align-items-center">
            {icons?.[1] && <IdsIcon name={icons[1]} size={24} />}
            <Paragraph text={priceDetails.productPrice ?? ''} bold={true} />
          </div>
        </div>
      </div>
    </div>
  );
}

function DrawerContent({
  view,
  texts: textsOverride,
  trackVisibilityElement,
  disableButton,
  setDisableButton,
  dispatchActionEvent,
}: DrawerContentProps) {
  // actions[0]/[1]/[2] are always close/minimize/open; content buttons start at index 3
  const rawActions = view.viewElements.actions;
  const contentActionsForRender = rawActions ? [rawActions[0], ...rawActions.slice(3)] : rawActions;
  const contentView: ContentView = {
    ...view,
    viewElements: { ...view.viewElements, actions: contentActionsForRender ?? null },
  };

  const { texts, linkStyledActions, buttonStyledActions } = renderContent({
    view: contentView,
    texts: textsOverride,
    trackVisibilityElement,
    actionsConfig: {
      dispatchActionEvent,
      skipFirstAction: true,
      disableButton,
      setDisableButton,
    },
  });

  const { priceDetails, icons } = view.viewElements;

  return (
    <div className="drawer-content">
      {texts.length > 0 && <div className="if flex-column gap-8">{texts}</div>}
      {linkStyledActions}
      <div className="if flex flex-column gap-16 justify-content-space-between drawer-flex-row">
        {priceDetails && <PriceDetails icons={icons} priceDetails={priceDetails} />}
        {buttonStyledActions.length > 0 && <div className="actions">{buttonStyledActions}</div>}
      </div>
    </div>
  );
}

export default DrawerContent;
